import { html, nothing } from "lit";
import type { SessionsListResult } from "../types.ts";
import {
  parseSessionKeyPath,
  sessionKeyFromPath,
  type SessionKeyPath,
} from "../chat/session-key-path.ts";
import { renderSessionPathNavigator } from "./session-path-navigator.ts";

export type SessionsScopeBarProps = {
  viewAll: boolean;
  scopeLinked: boolean;
  scopePath: SessionKeyPath;
  fleetSessionKey: string;
  openTabs: string[];
  sessions: SessionsListResult | null;
  connected: boolean;
  onToggleViewAll: (viewAll: boolean) => void;
  onToggleLinked: (linked: boolean) => void;
  onScopePathChange: (path: SessionKeyPath) => void;
};

export function renderSessionsScopeBar(props: SessionsScopeBarProps) {
  const activeSessionKey = sessionKeyFromPath(props.scopePath) ?? props.fleetSessionKey;

  return html`
    <div class="sessions-scope-bar fleet-session-bar logs-scope-bar" role="region" aria-label="Sessions scope">
      <div class="row" style="justify-content: space-between; align-items: center; gap: 8px; flex-wrap: wrap;">
        <div class="chip-row" style="margin: 0;">
          <label class="chip log-chip ${props.viewAll ? "active" : ""}">
            <input
              type="radio"
              name="sessions-view-mode"
              .checked=${props.viewAll}
              @change=${() => props.onToggleViewAll(true)}
            />
            <span>All sessions</span>
          </label>
          <label class="chip log-chip ${!props.viewAll ? "active" : ""}">
            <input
              type="radio"
              name="sessions-view-mode"
              .checked=${!props.viewAll}
              @change=${() => props.onToggleViewAll(false)}
            />
            <span>Session navigator</span>
          </label>
        </div>
        ${
          !props.viewAll
            ? html`
                <button
                  type="button"
                  class="btn btn--sm"
                  @click=${() => props.onToggleLinked(!props.scopeLinked)}
                >
                  ${props.scopeLinked ? "Unlink" : "Re-link to Fleet"}
                </button>
              `
            : nothing
        }
      </div>
      ${
        props.viewAll
          ? html`
              <p class="muted" style="margin: 8px 0 0; font-size: 12px">
                Showing every session returned by the gateway list (respects active/limit filters below).
              </p>
            `
          : html`
              <p class="muted" style="margin: 8px 0 0; font-size: 12px;">
                ${
                  props.scopeLinked
                    ? "Following Fleet session — table shows matching keys only"
                    : "Custom scope (unlinked from Fleet)"
                }
              </p>
            `
      }
      ${
        props.viewAll
          ? nothing
          : html`
              <div style="margin-top: 10px;">
                ${renderSessionPathNavigator({
                  activeSessionKey,
                  activePath: props.scopePath,
                  openTabs: props.openTabs,
                  sessions: props.sessions,
                  onSelectSession: (key) => {
                    if (props.scopeLinked) {
                      props.onToggleLinked(false);
                    }
                    props.onScopePathChange(parseSessionKeyPath(key));
                  },
                  showActiveFooter: false,
                  ariaLabel: "Sessions scope",
                  className: "fleet-session-path sessions-scope-path",
                })}
              </div>
            `
      }
      ${
        !props.connected
          ? html`
              <div class="muted" style="margin-top: 8px; font-size: 12px">
                Connect to the gateway to discover session keys.
              </div>
            `
          : nothing
      }
    </div>
  `;
}
